import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useT } from '@/i18n/use-t';
import {
  ACTIONS,
  ACTION_LABEL,
  ACTION_LABEL_KEY,
  CONDITION_KINDS,
  CONDITION_LABEL,
  CONDITION_LABEL_KEY,
  TRIGGER_LABEL,
  TRIGGER_LABEL_KEY,
  defaultCondition,
  newRule,
  triggersForSurface,
  type FlowAction,
  type FlowCondition,
  type FlowConditionKind,
  type FlowRule,
  type FlowSurface,
  type FlowTrigger,
} from './flow-vocab';

/**
 * RFL-131 — the graphical (node-RED-style) editor. Each rule is a lane of
 * three wired nodes: trigger → condition → action. Editing a node edits
 * the rule; both this canvas and the JSON view bind to the same `rules`
 * array, so flipping between them never loses edits.
 */
export function FlowCanvas({
  surface,
  rules,
  onChange,
}: {
  surface: FlowSurface;
  rules: FlowRule[];
  onChange: (rules: FlowRule[]) => void;
}) {
  const t = useT('admin_ui');
  const sorted = [...rules].sort((a, b) => a.order - b.order);

  function patch(id: string, next: Partial<FlowRule>): void {
    onChange(rules.map((r) => (r.id === id ? { ...r, ...next } : r)));
  }
  function remove(id: string): void {
    onChange(rules.filter((r) => r.id !== id).map((r, i) => ({ ...r, order: i })));
  }
  function move(id: string, dir: -1 | 1): void {
    const idx = sorted.findIndex((r) => r.id === id);
    const swap = idx + dir;
    if (swap < 0 || swap >= sorted.length) return;
    const next = [...sorted];
    [next[idx], next[swap]] = [next[swap]!, next[idx]!];
    onChange(next.map((r, i) => ({ ...r, order: i })));
  }
  function add(): void {
    onChange([...rules, newRule(surface, rules.length)]);
  }

  const triggers = triggersForSurface(surface);

  return (
    <div className="space-y-4">
      {sorted.length === 0 && (
        <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          {t(
            'terminalFlows.canvas.empty',
            'No rules yet. Add one to wire a trigger through a condition to an action.',
          )}
        </p>
      )}

      {sorted.map((rule) => (
        <div
          key={rule.id}
          className={`flex flex-wrap items-stretch gap-0 rounded-card border border-border bg-card p-3 ${
            rule.enabled ? '' : 'opacity-60'
          }`}
        >
          <div className="flex flex-1 flex-wrap items-center gap-0">
            {/* Trigger node */}
            <Node tone="trigger" title={t('terminalFlows.canvas.when', 'WHEN')}>
              <Select
                aria-label={t('terminalFlows.canvas.trigger', 'Trigger')}
                value={rule.trigger}
                onChange={(e) => patch(rule.id, { trigger: e.target.value as FlowTrigger })}
              >
                {triggers.map((trigger) => (
                  <option key={trigger} value={trigger}>
                    {t(TRIGGER_LABEL_KEY[trigger], TRIGGER_LABEL[trigger])}
                  </option>
                ))}
              </Select>
            </Node>

            <Wire />

            {/* Condition node */}
            <Node tone="condition" title={t('terminalFlows.canvas.if', 'IF')}>
              <Select
                aria-label={t('terminalFlows.canvas.condition', 'Condition')}
                value={rule.condition.kind}
                onChange={(e) =>
                  patch(rule.id, {
                    condition: defaultCondition(e.target.value as FlowConditionKind),
                  })
                }
              >
                {CONDITION_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(CONDITION_LABEL_KEY[k], CONDITION_LABEL[k])}
                  </option>
                ))}
              </Select>
              <ConditionParams
                condition={rule.condition}
                onChange={(c) => patch(rule.id, { condition: c })}
              />
            </Node>

            <Wire />

            {/* Action node */}
            <Node tone="action" title={t('terminalFlows.canvas.then', 'THEN')}>
              <Select
                aria-label={t('terminalFlows.canvas.action', 'Action')}
                value={rule.action}
                onChange={(e) => patch(rule.id, { action: e.target.value as FlowAction })}
              >
                {ACTIONS.map((a) => (
                  <option key={a} value={a}>
                    {t(ACTION_LABEL_KEY[a], ACTION_LABEL[a])}
                  </option>
                ))}
              </Select>
            </Node>
          </div>

          {/* Lane controls */}
          <div className="flex flex-col items-center justify-center gap-1 pl-3">
            <label className="flex items-center gap-1 text-xs text-muted-foreground">
              <input
                type="checkbox"
                checked={rule.enabled}
                onChange={(e) => patch(rule.id, { enabled: e.target.checked })}
                aria-label={t('terminalFlows.canvas.enabled', 'Enabled')}
              />
              {t('terminalFlows.canvas.on', 'on')}
            </label>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => move(rule.id, -1)}
                title={t('terminalFlows.canvas.moveUp', 'Move up')}
              >
                ↑
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => move(rule.id, 1)}
                title={t('terminalFlows.canvas.moveDown', 'Move down')}
              >
                ↓
              </Button>
            </div>
            <Button variant="outline" size="sm" onClick={() => remove(rule.id)}>
              {t('terminalFlows.canvas.remove', 'Remove')}
            </Button>
          </div>
        </div>
      ))}

      <Button onClick={add}>{t('terminalFlows.canvas.addRule', '+ Add rule')}</Button>
    </div>
  );
}

function Node({
  tone,
  title,
  children,
}: {
  tone: 'trigger' | 'condition' | 'action';
  title: string;
  children: React.ReactNode;
}) {
  const toneClass =
    tone === 'trigger'
      ? 'border-sky-500/40 bg-sky-500/5'
      : tone === 'condition'
        ? 'border-amber-500/40 bg-amber-500/5'
        : 'border-emerald-500/40 bg-emerald-500/5';
  return (
    <div className={`min-w-[180px] space-y-1.5 rounded-md border p-2.5 ${toneClass}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      {children}
    </div>
  );
}

/** A short connector wire between two nodes. */
function Wire() {
  return (
    <svg width="28" height="20" className="shrink-0 self-center" aria-hidden="true">
      <line
        x1="0"
        y1="10"
        x2="22"
        y2="10"
        stroke="currentColor"
        strokeWidth="2"
        className="text-border"
      />
      <polygon points="22,5 28,10 22,15" fill="currentColor" className="text-border" />
    </svg>
  );
}

function ConditionParams({
  condition,
  onChange,
}: {
  condition: FlowCondition;
  onChange: (c: FlowCondition) => void;
}) {
  const t = useT('admin_ui');
  if (condition.kind === 'amount_over') {
    return (
      <Input
        aria-label={t('terminalFlows.canvas.amountMinorUnits', 'Amount (minor units)')}
        type="number"
        min={0}
        value={condition.amountMinor}
        onChange={(e) => onChange({ kind: 'amount_over', amountMinor: Number(e.target.value) })}
        placeholder={t('terminalFlows.canvas.amountMinorPlaceholder', 'amount (minor)')}
      />
    );
  }
  if (condition.kind === 'cashier_role_not') {
    return (
      <Input
        aria-label={t('terminalFlows.canvas.role', 'Role')}
        value={condition.role}
        onChange={(e) => onChange({ kind: 'cashier_role_not', role: e.target.value })}
        placeholder={t('terminalFlows.canvas.rolePlaceholder', 'role')}
      />
    );
  }
  if (condition.kind === 'time_of_day_in') {
    return (
      <div className="flex items-center gap-1">
        <Input
          aria-label={t('terminalFlows.canvas.from', 'From')}
          value={condition.from}
          onChange={(e) => onChange({ ...condition, from: e.target.value })}
          placeholder="HH:MM"
        />
        <span className="text-xs text-muted-foreground">→</span>
        <Input
          aria-label={t('terminalFlows.canvas.to', 'To')}
          value={condition.to}
          onChange={(e) => onChange({ ...condition, to: e.target.value })}
          placeholder="HH:MM"
        />
      </div>
    );
  }
  return null;
}
